import React, { useEffect, useState } from 'react';

import { ReactComponent as ActionStopTransactionIcon } from '../../assets/icons/action-stop-transaction.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableChargingSession,
  ChargingSessionRequest,
  RequestSessionInterFace,
  StopChargingRequest,
} from '../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchChargingSessions,
  setChargingSessionFilterData,
  updateChargingFilters,
  fetchListFilters,
} from '../../redux/slice/ChargingSessionSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotificaion';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import chargingSessionApi from '../../utils/api/charging-session';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Function to list current and upcoming charging transactions
 * @returns
 */
function ChargingSession() {
  const dispatch = useAppDispatch();
  const {
    chargingSessions,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
  } = useAppSelector((state) => state.chargingSession);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchChargingSession = (data: ChargingSessionRequest) => {
    dispatch(fetchChargingSessions(data));
  };

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestSessionInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchChargingSessions({
          ...data,
          // facilityId: selectedFacility?._id,
        }),
      );
    }
  };

  /**
   * Handles the pagination
   *
   */
  const handlePageChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchChargingSession({
      ...queryParameters,
      page: changePage,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchChargingSession({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Renders the custom data for the serial number column
   * @param {AvailableChargingSession} device - The device data
   * @param {number} index - The current row index
   * @returns {JSX.Element} The rendered serial number cell
   */
  const renderSerialNumber = (_: AvailableChargingSession, index: number) => (
    <div className="table-row-each-data">{index + 1}</div>
  );

  /**
   * Handle the modal close function
   */
  const handleCloseModal = () => {
    setModalBoxStatus('close');
  };

  /**
   * Handle the Stop Charging Transaction function
   */
  const handleStopCharging = (requestData: StopChargingRequest) => {
    chargingSessionApi
      .stopCharging(requestData)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Stop Transaction Request Sent"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCloseModal}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };
  /**
   * Handle the Stop Charging Transaction function
   */
  const handleStopChargingWarning = (chargingSession: AvailableChargingSession) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={chargingSession.id}
          title="Are you sure want to stop this charging?"
          description="Stopping the charging cannot be undone. Are you sure you want to proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          // submitSvgIcon={<ActionStopTransactionIcon />}
          submitButtonOnClick={(id) =>
            id &&
            handleStopCharging({
              chargingPointSn: chargingSession.chargingPointSn,
              transactionId: chargingSession.transactionId,
            })
          }
          submitButtonLabel="Stop Charging"
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Renders the custom data for the actions column
   * @param {AvailableChargingSession} chargingSession - The charging session data
   * @returns {JSX.Element} The rendered actions cell
   */
  const renderActions = (chargingSession: AvailableChargingSession) => (
    <div className="action-button-group">
      {chargingSession.status === 'inProgress' && (
        <div
          className="action-button-item view-color"
          onClick={() => handleStopChargingWarning(chargingSession as AvailableChargingSession)}
          role="presentation">
          <ActionStopTransactionIcon />
        </div>
      )}
    </div>
  );

  const column = [
    {
      label: 'S. No',
      accessorKey: 'sno' as keyof AvailableChargingSession,
      customData: renderSerialNumber,
      columnWidth: 45,
    },
    // {
    //   label: 'Date',
    //   accessorKey: 'date' as keyof AvailableChargingSession,
    //   sortAllow: false,
    // },
    // {
    //   label: 'Time',
    //   accessorKey: 'time' as keyof AvailableChargingSession,
    //   sortAllow: false,
    // },
    {
      label: 'Charging Started At',
      accessorKey: 'transactionStartsAt' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'Charging Ended At',
      accessorKey: 'transactionEndsAt' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'Facility name',
      accessorKey: 'facilityTagName' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'Station name',
      accessorKey: 'chargingStation' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'Device name',
      accessorKey: 'chargingPointName' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'User Name',
      accessorKey: 'endConsumer' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: 'Status',
      accessorKey: 'status' as keyof AvailableChargingSession,
    },
    {
      label: 'Actions',
      customData: renderActions,
      accessorKey: '' as keyof AvailableChargingSession,
      columnWidth: 150,
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setChargingSessionFilterData({
        ...sortData,
      }),
    );
    fetchChargingSession({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="charging-session-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <PageTitle title="Charging Session" />
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={chargingSessions}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateChargingFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default ChargingSession;
